import { gql } from "@apollo/client";
import { AuthProvider } from "react-admin";
import { apolloClient } from "./apolloClient";
import {
  clearAuthSession,
  ensureRememberedSessionExpiry,
  getAuthStorage,
  saveAuthSession,
} from "./authSession";

const LOGIN_ERROR = "Correo o contraseña incorrectos";
const SESSION_ERROR = "Tu sesión ha caducado. Vuelve a iniciar sesión";

const authProvider: AuthProvider = {
  login: async ({ username, password, rememberMe = false }: any) => {
    try {
      clearAuthSession();
      const request = await apolloClient.mutate({
        mutation: gql`
          mutation login($data: LoginInput!) {
            login(data: $data) {
              accessToken
              refreshToken
              user {
                id
                email
                role
                clinicaId
                franquiciaId
              }
            }
          }
        `,
        variables: {
          data: { email: username, password },
        },
      });

      if (request.errors) {
        throw new Error(JSON.stringify(request.errors));
      }

      const { accessToken, refreshToken, user } = request.data.login;
      const { email, id, role, clinicaId, franquiciaId } = user;

      if (role === "USER") {
        throw new Error("El panel está reservado para administradores");
      }

      saveAuthSession({
        accessToken,
        refreshToken,
        auth: JSON.stringify({ email, id, role, clinicaId, franquiciaId }),
        rememberMe,
      });
    } catch (error) {
      clearAuthSession();
      throw new Error(LOGIN_ERROR);
    }
  },

  logout: async () => {
    clearAuthSession();
    await apolloClient.clearStore();
  },

  checkAuth: async () => {
    const storage = getAuthStorage();

    if (!storage || !ensureRememberedSessionExpiry(storage)) {
      clearAuthSession();
      throw new Error(SESSION_ERROR);
    }

    const accessToken = storage.getItem("accessToken")!;
    const refreshToken = storage.getItem("refreshToken")!;

    try {
      const { exp } = parseJwt(accessToken);
      if (typeof exp !== "number") {
        throw new Error(SESSION_ERROR);
      }

      if (exp * 1000 > Date.now() + 4 * 60 * 60 * 1000) {
        return;
      }

      const request = await apolloClient.mutate({
        mutation: gql`
          mutation refreshToken($token: JWT!) {
            refreshToken(token: $token) {
              accessToken
              refreshToken
            }
          }
        `,
        variables: {
          token: refreshToken,
        },
      });

      const refreshedTokens = request.data?.refreshToken;
      if (!refreshedTokens?.accessToken || !refreshedTokens?.refreshToken) {
        throw new Error(SESSION_ERROR);
      }

      storage.setItem("accessToken", refreshedTokens.accessToken);
      storage.setItem("refreshToken", refreshedTokens.refreshToken);
    } catch (error) {
      clearAuthSession();
      throw new Error(SESSION_ERROR);
    }
  },

  getIdentity: async () => {
    try {
      const storage = getAuthStorage();
      if (!storage) {
        throw new Error(SESSION_ERROR);
      }

      const { id, email, role, clinicaId, franquiciaId } = JSON.parse(
        storage.getItem("auth")!
      );

      return {
        id,
        fullName: email,
        role,
        email,
        clinicaId,
        franquiciaId,
        avatar:
          "https://pbs.twimg.com/profile_images/831173492968140804/43M7c5j__400x400.jpg",
      };
    } catch (error) {
      clearAuthSession();
      throw error;
    }
  },

  checkError: (error: any) => {
    const status = error.status;

    if (status === 401 || status === 403) {
      clearAuthSession();
      return Promise.reject();
    }
    return Promise.resolve();
  },

  getPermissions: () => {
    try {
      const storage = getAuthStorage();
      if (!storage) {
        throw new Error(SESSION_ERROR);
      }

      const { role } = JSON.parse(storage.getItem("auth")!);
      return Promise.resolve(role);
    } catch (error) {
      clearAuthSession();
      return Promise.reject(error);
    }
  },
};

export default authProvider;

export function parseJwt(token: string) {
  const base64Url = token.split(".")[1];
  if (!base64Url) {
    throw new Error("Token inválido");
  }

  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (character) {
        return (
          "%" + ("00" + character.charCodeAt(0).toString(16)).slice(-2)
        );
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}
